import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Query defaults tuned for "feels instant":
 *
 * - `staleTime` is generous. Every screen in Billable shows data that changes
 *   at human speed; refetching on every mount is what makes an app feel like
 *   it's constantly reloading. Mutations invalidate precisely instead.
 * - `placeholderData: keepPreviousData` is applied per-query on paginated /
 *   date-ranged lists so navigating a week at a time never blanks the screen.
 * - Auth errors are never retried; there's nothing to retry.
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry(failureCount, error) {
          if (error instanceof ApiError) {
            if (error.isAuth || error.status === 404) return false;
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 2;
        }
      },
      mutations: {
        retry: 0
      }
    }
  });
}

/**
 * Query key factory. Centralized so optimistic updates and invalidations can
 * never drift out of sync with the queries they're patching — the single most
 * common source of "the UI didn't update" bugs.
 */
export const qk = {
  session: ['session'] as const,

  clients: ['clients'] as const,
  activities: ['activities'] as const,
  billables: ['billables'] as const,
  creditFeeTemplates: ['credit-fee-templates'] as const,
  teamUsers: ['team-users'] as const,

  daycards: (from: string, to: string) => ['daycards', from, to] as const,
  daycardsRoot: ['daycards'] as const,

  /**
   * The pool the New invoice screen draws from: one client's not-yet-invoiced
   * entries. Deliberately NOT nested under `daycardsRoot`: the optimistic
   * patches in hooks/useEntries.ts run against every cache under that prefix
   * and assume a paginated `{ items }` shape, and this one is a bare array.
   * Every invoice mutation invalidates `uninvoicedEntriesRoot` explicitly, and
   * the query itself is always refetched on mount (see CreateInvoicePage).
   */
  uninvoicedEntries: (clientId: string) => ['uninvoiced-entries', clientId] as const,
  uninvoicedEntriesRoot: ['uninvoiced-entries'] as const,

  timer: ['timer'] as const,

  invoices: (filters?: Record<string, unknown>) =>
    filters && Object.keys(filters).length ? (['invoices', filters] as const) : (['invoices'] as const),
  invoicesRoot: ['invoices'] as const,
  invoice: (id: string) => ['invoice', id] as const,
  invoiceEntries: (id: string) => ['invoice', id, 'entries'] as const,
  invoicePdf: (id: string) => ['invoice', id, 'pdf'] as const,
  invoiceEmails: (id: string) => ['invoice', id, 'emails'] as const,

  quickInvoices: ['quick-invoices'] as const,
  quickInvoice: (id: string) => ['quick-invoice', id] as const,

  creditsAndFees: (filters?: Record<string, unknown>) =>
    filters ? (['credits-and-fees', filters] as const) : (['credits-and-fees'] as const),
  creditsAndFeesRoot: ['credits-and-fees'] as const,

  balances: ['balances'] as const,

  reports: (name: string, filters?: unknown) => ['reports', name, filters ?? {}] as const,
  reportsRoot: ['reports'] as const,
  reportFilters: ['reports', 'filters'] as const,

  emails: (filters?: Record<string, unknown>) => ['emails', filters ?? {}] as const,
  emailsRoot: ['emails'] as const,
  email: (id: string) => ['email', id] as const,

  personalHours: (from: string, to: string) => ['personal-hours', from, to] as const,
  personalHoursRoot: ['personal-hours'] as const,

  settings: ['settings'] as const,
  invoiceSettings: ['invoice-settings'] as const,
  remindersRoot: ['reminders'] as const,
  upcomingReminders: ['reminders', 'upcoming'] as const,
  invoiceReminders: (invoiceId: string) => ['reminders', 'invoice', invoiceId] as const,
  companyInfo: ['company-info'] as const,
  team: ['team'] as const,
  teamMembers: ['team', 'members'] as const,
  teamInvites: ['team', 'invites'] as const,
  subscription: ['subscription'] as const,
  tierLimits: ['tier-limits'] as const,

  setup: ['setup'] as const,
  imports: ['imports'] as const,
  importsPending: ['imports', 'pending'] as const,
  import: (id: string) => ['import', id] as const,
  providers: ['providers'] as const
};
